import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Building2,
    MapPin,
    Phone,
    Mail,
    Car,
    Edit2,
    X,
    Check,
    Loader2,
    AlertCircle,
    Hash,
    Save,
    CreditCard,
    ExternalLink,
    Crown,
    Zap,
    Users,
    FileText,
    Clock,
    AlertTriangle,
    RefreshCw,
    Download,
    Receipt,
    CheckCircle2,
    Calendar,
    FileSpreadsheet,
} from 'lucide-react';
import PageShell from './PageShell';
import ImportsTab from './imports/ImportsTab';
import BrandingCard from './BrandingCard';
import VoiceBookingCard from './VoiceBookingCard';
import axiosInstance from '../services/axiosInstance';
import {
    getSubscriptionApi,
    getAvailablePlansApi,
    getUsageApi,
    createCheckoutSessionApi,
    createBillingPortalSessionApi,
    getInvoicesApi,
    formatCurrency,
    formatDate,
    getStatusBadgeClass,
    requiresAction
} from '../services/billingApi';

const ORGANISATION_TYPES = [
    { value: 'MECHANIC_WORKSHOP', label: 'Mechanic Workshop' },
    { value: 'CAR_WASH', label: 'Car Wash & Clean' },
];

const ORGANISATION_TYPE_LABELS = ORGANISATION_TYPES.reduce(
    (acc, t) => ({ ...acc, [t.value]: t.label }),
    {}
);

/**
 * One line of the Plan usage panel: what is used against what the plan allows.
 *
 * Module scope, not nested in the page, so it is not rebuilt on every render and
 * can be tested without standing up the whole Organisation screen.
 *
 * @param hint optional second line. The service-profile row needs one: the figure
 *        counts what the workshop BUILT, and without a word of explanation it reads
 *        "0 / 1" beside a page listing seven profiles.
 */
export const UsageStat = ({ label, res, hint = null }) => {
    let value = '—';
    let danger = false;
    if (res) {
        value = res.unlimited ? 'Unlimited' : `${res.used} / ${res.limit}`;
        danger = res.atLimit;
    }
    return (
        <div className="flex items-start justify-between gap-4">
            <span className="text-sm text-gray-500">
                {label}
                {/* The count alone can be misleading — see the service-profile hint. */}
                {hint && <span className="block text-xs text-gray-400 mt-0.5">{hint}</span>}
            </span>
            <span className={`text-sm font-semibold whitespace-nowrap ${danger ? 'text-red-600' : 'text-gray-900'}`}>
                {value}
            </span>
        </div>
    );
};

const OrganisationPage = () => {
    // URL params for subscription redirect handling
    const [searchParams, setSearchParams] = useSearchParams();

    // Organisation state
    const [organisation, setOrganisation] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Subscription notification state
    const [subscriptionNotification, setSubscriptionNotification] = useState(null);

    // Which tab is showing. "details" is the organisation's own record; "plan" holds
    // everything commercial — usage, current plan, payouts, branding, phone bookings.
    //
    // Stripe checkout returns to this page with ?subscription=success|cancelled. That
    // param is read here during the FIRST render, before the effect below clears it,
    // so a user coming back from checkout lands on the tab that actually shows their
    // new plan instead of on Details with a success banner and nothing to look at.
    const [activeTab, setActiveTab] = useState(
        () => (searchParams.get('subscription') ? 'plan' : 'details')
    );

    const ORG_TABS = [
        { id: 'details', label: 'Details', icon: Building2 },
        { id: 'plan', label: 'Plan & billing', icon: CreditCard },
        { id: 'imports', label: 'Imports', icon: FileSpreadsheet },
    ];

    // Edit states
    const [isEditingDetails, setIsEditingDetails] = useState(false);
    const [isEditingAddress, setIsEditingAddress] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    // Form state for editing
    const [formData, setFormData] = useState({
        name: '',
        organisationType: 'MECHANIC_WORKSHOP',
        abn: '',
        phone: '',
        email: '',
        address: '',
        suburb: '',
        state: '',
        postcode: '',
    });

    // Subscription state
    const [subscription, setSubscription] = useState(null);
    const [availablePlans, setAvailablePlans] = useState([]);
    const [usage, setUsage] = useState(null);
    const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
    const [subscriptionError, setSubscriptionError] = useState(null);

    // Invoices state
    const [invoices, setInvoices] = useState([]);
    const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
    const [showInvoices, setShowInvoices] = useState(false);

    // Plans modal state
    const [showPlansModal, setShowPlansModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
    const [checkoutError, setCheckoutError] = useState(null);

    // Billing portal state
    const [isOpeningPortal, setIsOpeningPortal] = useState(false);

    // ── Work Schedule ────────────────────────────────────────────────────────
    const DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
    const DAY_LABEL = { MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed', THURSDAY: 'Thu', FRIDAY: 'Fri', SATURDAY: 'Sat', SUNDAY: 'Sun' };
    const DAY_FULL  = { MONDAY: 'Monday', TUESDAY: 'Tuesday', WEDNESDAY: 'Wednesday', THURSDAY: 'Thursday', FRIDAY: 'Friday', SATURDAY: 'Saturday', SUNDAY: 'Sunday' };

    const buildDefaultSchedule = () => DAYS.map(d => ({
        dayOfWeek: d,
        isClosed:  d === 'SATURDAY' || d === 'SUNDAY',
        openTime:  '08:00',
        closeTime: '17:00',
    }));

    const [schedule,           setSchedule]           = useState(buildDefaultSchedule());
    const [scheduleConfigured, setScheduleConfigured] = useState(false);
    const [isEditingSchedule,  setIsEditingSchedule]  = useState(false);
    const [scheduleForm,       setScheduleForm]       = useState(buildDefaultSchedule());
    const [isLoadingSchedule,  setIsLoadingSchedule]  = useState(true);
    const [isSavingSchedule,   setIsSavingSchedule]   = useState(false);
    const [scheduleError,      setScheduleError]      = useState(null);
    const [scheduleSaveError,  setScheduleSaveError]  = useState(null);

    // Handle subscription redirect params (from Stripe checkout)
    useEffect(() => {
        const subscriptionStatus = searchParams.get('subscription');

        if (subscriptionStatus === 'success') {
            setSubscriptionNotification({
                type: 'success',
                message: 'Subscription activated successfully! Your plan has been updated.'
            });
            setSearchParams({});
            fetchSubscription();
        } else if (subscriptionStatus === 'cancelled') {
            setSubscriptionNotification({
                type: 'info',
                message: 'Subscription checkout was cancelled.'
            });
            setSearchParams({});
        }
    }, [searchParams, setSearchParams]);

    // Auto-dismiss notification after 5 seconds
    useEffect(() => {
        if (subscriptionNotification) {
            const timer = setTimeout(() => {
                setSubscriptionNotification(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [subscriptionNotification]);

    // Fetch organisation on mount
    useEffect(() => {
        fetchOrganisation();
        fetchSubscription();
        fetchSchedule();
    }, []);

    // Check for success/canceled query params (return from Stripe)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('success') === 'true') {
            // Refresh subscription after successful checkout
            fetchSubscription();
            // Clean up URL
            window.history.replaceState({}, '', window.location.pathname);
        } else if (params.get('canceled') === 'true') {
            // User canceled checkout
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []);

    const fetchOrganisation = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get('/organisation');
            const orgData = response.data.organisation;
            setOrganisation(orgData);
            setFormData({
                name: orgData.name || '',
                organisationType: orgData.organisationType || 'MECHANIC_WORKSHOP',
                abn: orgData.abn || '',
                phone: orgData.phone || '',
                email: orgData.email || '',
                address: orgData.address || '',
                suburb: orgData.suburb || '',
                state: orgData.state || '',
                postcode: orgData.postcode || '',
            });
        } catch (err) {
            console.error('Failed to fetch organisation:', err);
            setError(err.response?.data?.message || 'Failed to load organisation details');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchSubscription = async () => {
        setIsLoadingSubscription(true);
        setSubscriptionError(null);
        try {
            const [subData, plansData, usageData] = await Promise.all([
                getSubscriptionApi(),
                getAvailablePlansApi(),
                getUsageApi().catch(() => null)  // usage is non-critical; don't fail the page
            ]);
            setSubscription(subData);
            setAvailablePlans(plansData.plans || []);
            setUsage(usageData);
            setSelectedPlan(subData?.plan || 'ENTRY');
        } catch (err) {
            console.error('Failed to fetch subscription:', err);
            setSubscriptionError(err.response?.data?.message || 'Failed to load subscription');
        } finally {
            setIsLoadingSubscription(false);
        }
    };

    const fetchInvoices = async () => {
        setIsLoadingInvoices(true);
        try {
            const data = await getInvoicesApi(10);
            setInvoices(data.invoices || []);
        } catch (err) {
            console.error('Failed to fetch invoices:', err);
        } finally {
            setIsLoadingInvoices(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        setSaveError(null);
        try {
            const response = await axiosInstance.put('/organisation', {
                organisationName: formData.name,
                organisationType: formData.organisationType,
                orgAddress: formData.address,
                orgCity: formData.suburb,
                orgState: formData.state,
                orgPostcode: formData.postcode,
                abn: formData.abn?.replace(/\s/g, '') || null,
                phone: formData.phone || null,
            });

            const orgData = response.data.organisation;
            setOrganisation(orgData);
            setFormData({
                name: orgData.name || '',
                organisationType: orgData.organisationType || 'MECHANIC_WORKSHOP',
                abn: orgData.abn || '',
                phone: orgData.phone || '',
                email: orgData.email || '',
                address: orgData.address || '',
                suburb: orgData.suburb || '',
                state: orgData.state || '',
                postcode: orgData.postcode || '',
            });
            setIsEditingDetails(false);
            setIsEditingAddress(false);
        } catch (err) {
            console.error('Failed to update organisation:', err);
            setSaveError(err.response?.data?.message || 'Failed to save changes');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancelEdit = () => {
        if (organisation) {
            setFormData({
                name: organisation.name || '',
                organisationType: organisation.organisationType || 'MECHANIC_WORKSHOP',
                abn: organisation.abn || '',
                phone: organisation.phone || '',
                email: organisation.email || '',
                address: organisation.address || '',
                suburb: organisation.suburb || '',
                state: organisation.state || '',
                postcode: organisation.postcode || '',
            });
        }
        setIsEditingDetails(false);
        setIsEditingAddress(false);
        setSaveError(null);
    };

    // ── Work Schedule handlers ───────────────────────────────────────────────

    // Backend sends "HH:mm:ss" — trim to "HH:mm" for <input type="time">
    const trimTime = (t) => (t ? t.slice(0, 5) : '08:00');

    const mapResponseToForm = (days) =>
        DAYS.map(day => {
            const row = days.find(d => d.dayOfWeek === day);
            return row
                ? { dayOfWeek: day, isClosed: row.isClosed, openTime: trimTime(row.openTime), closeTime: trimTime(row.closeTime) }
                : { dayOfWeek: day, isClosed: true, openTime: '08:00', closeTime: '17:00' };
        });

    const fetchSchedule = async () => {
        setIsLoadingSchedule(true);
        setScheduleError(null);
        try {
            const res = await axiosInstance.get('/organisation/schedule');
            const { scheduleConfigured: configured, days } = res.data;
            setScheduleConfigured(configured);
            if (configured && days?.length) {
                const mapped = mapResponseToForm(days);
                setSchedule(mapped);
                setScheduleForm(mapped);
            }
        } catch (err) {
            console.error('Failed to fetch schedule:', err);
            setScheduleError(err.response?.data?.message || 'Failed to load work schedule');
        } finally {
            setIsLoadingSchedule(false);
        }
    };

    const handleScheduleDayChange = (dayOfWeek, field, value) =>
        setScheduleForm(prev => prev.map(d => d.dayOfWeek === dayOfWeek ? { ...d, [field]: value } : d));

    const handleSaveSchedule = async () => {
        // Client-side validation
        for (const day of scheduleForm) {
            if (!day.isClosed) {
                if (!day.openTime || !day.closeTime) {
                    setScheduleSaveError(`${DAY_FULL[day.dayOfWeek]}: open and close times are required.`);
                    return;
                }
                if (day.openTime >= day.closeTime) {
                    setScheduleSaveError(`${DAY_FULL[day.dayOfWeek]}: close time must be after open time.`);
                    return;
                }
            }
        }
        setIsSavingSchedule(true);
        setScheduleSaveError(null);
        try {
            const res = await axiosInstance.put('/organisation/schedule', {
                days: scheduleForm.map(d => ({
                    dayOfWeek: d.dayOfWeek,
                    isClosed:  d.isClosed,
                    openTime:  d.isClosed ? null : d.openTime,
                    closeTime: d.isClosed ? null : d.closeTime,
                })),
            });
            const { scheduleConfigured: configured, days } = res.data;
            setScheduleConfigured(configured);
            const mapped = mapResponseToForm(days);
            setSchedule(mapped);
            setScheduleForm(mapped);
            setIsEditingSchedule(false);
        } catch (err) {
            console.error('Failed to save schedule:', err);
            setScheduleSaveError(err.response?.data?.message || 'Failed to save schedule. Please try again.');
        } finally {
            setIsSavingSchedule(false);
        }
    };

    const handleCancelScheduleEdit = () => {
        setScheduleForm(schedule);
        setScheduleSaveError(null);
        setIsEditingSchedule(false);
    };

    // Subscription handlers
    const handleSelectPlan = async (planId) => {
        if (planId === subscription?.plan) return;

        setIsProcessingCheckout(true);
        setCheckoutError(null);

        try {
            const data = await createCheckoutSessionApi(planId);

            // Check if subscription was updated directly (no checkout needed)
            if (data.updated) {
                // Subscription was updated directly - show success and refresh
                setSubscriptionNotification({
                    type: 'success',
                    message: data.message || `Plan updated to ${data.newPlan}`
                });
                // Refresh subscription data
                await fetchSubscription();
                setIsProcessingCheckout(false);
            } else if (data.url) {
                // Redirect to Stripe Checkout for new subscription
                window.location.href = data.url;
            }
        } catch (err) {
            console.error('Failed to create checkout session:', err);
            setCheckoutError(err.response?.data?.message || 'Failed to start checkout');
            setIsProcessingCheckout(false);
        }
    };

    const handleOpenBillingPortal = async () => {
        setIsOpeningPortal(true);
        try {
            const data = await createBillingPortalSessionApi();
            if (data.url) {
                window.location.href = data.url;
            }
        } catch (err) {
            console.error('Failed to open billing portal:', err);
            alert(err.response?.data?.message || 'Failed to open billing portal');
        } finally {
            setIsOpeningPortal(false);
        }
    };

    const handleViewInvoices = () => {
        setShowInvoices(true);
        if (invoices.length === 0) {
            fetchInvoices();
        }
    };

    // Format ABN with spaces (XX XXX XXX XXX)
    const formatAbn = (abn) => {
        if (!abn) return '—';
        const cleaned = abn.replace(/\s/g, '');
        if (cleaned.length !== 11) return abn;
        return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8, 11)}`;
    };

    // Get plan icon
    const getPlanIcon = (planId) => {
        switch (planId?.toUpperCase()) {
            case 'ENTERPRISE': return Crown;
            case 'PROFESSIONAL': return Zap;
            case 'STARTER': return Users;
            default: return FileText;
        }
    };

    // Info item component for display mode
    const InfoItem = ({ icon: Icon, label, value }) => (
        <div className="flex items-start gap-3 py-3">
            <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon size={16} className="text-gray-500" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-0.5">{label}</p>
                <p className="text-sm font-medium text-gray-900">{value || '—'}</p>
            </div>
        </div>
    );

    // Editable field component
    const EditableField = ({ icon: Icon, label, field, value, placeholder, type = 'text' }) => (
        <div className="flex items-start gap-3 py-3">
            <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon size={16} className="text-gray-500" />
            </div>
            <div className="flex-1 min-w-0">
                <label className="text-xs text-gray-500 mb-1 block">{label}</label>
                <input
                    type={type}
                    value={value}
                    onChange={(e) => handleInputChange(field, e.target.value)}
                    placeholder={placeholder}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
            </div>
        </div>
    );

    // Plans Modal Component
    const PlansModal = () => (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={() => !isProcessingCheckout && setShowPlansModal(false)}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Choose your plan</h2>
                        <p className="text-sm text-gray-500">Select the plan that best fits your workshop</p>
                    </div>
                    <button
                        onClick={() => setShowPlansModal(false)}
                        disabled={isProcessingCheckout}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                    >
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                {/* Error message */}
                {checkoutError && (
                    <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700">
                        <AlertCircle size={16} />
                        {checkoutError}
                    </div>
                )}

                {/* Plans grid */}
                <div className="p-6">
                    {isLoadingSubscription ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 size={32} className="animate-spin text-emerald-600" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {availablePlans.map((plan) => {
                                const isCurrent = plan.id === subscription?.plan;
                                const isSelected = selectedPlan === plan.id;
                                const PlanIcon = getPlanIcon(plan.id);

                                return (
                                    <div
                                        key={plan.id}
                                        className={`relative rounded-xl border-2 p-5 transition-all cursor-pointer ${
                                            isSelected
                                                ? 'border-emerald-500 bg-emerald-50/50'
                                                : 'border-gray-200 hover:border-gray-300'
                                        } ${plan.isPopular ? 'ring-2 ring-emerald-500 ring-offset-2' : ''}`}
                                        onClick={() => !isProcessingCheckout && setSelectedPlan(plan.id)}
                                    >
                                        {plan.isPopular && (
                                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-emerald-500 text-white text-xs font-semibold rounded-full">
                                                Most Popular
                                            </div>
                                        )}

                                        {isCurrent && (
                                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gray-700 text-white text-xs font-semibold rounded-full">
                                                Current Plan
                                            </div>
                                        )}

                                        <div className="mb-4">
                                            <div className="flex items-center gap-2 mb-1">
                                                <PlanIcon size={18} className="text-emerald-600" />
                                                <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                                            </div>
                                            <p className="text-sm text-gray-500">{plan.description}</p>
                                        </div>

                                        <div className="mb-4">
                                            <span className="text-3xl font-bold text-gray-900">
                                                {formatCurrency(plan.price, plan.currency)}
                                            </span>
                                            {plan.price > 0 && (
                                                <span className="text-gray-500 text-sm">/{plan.interval}</span>
                                            )}
                                        </div>

                                        <ul className="space-y-2 mb-5">
                                            {plan.features?.map((feature, idx) => (
                                                <li key={idx} className="flex items-start gap-2 text-sm">
                                                    <Check size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                                                    <span className="text-gray-600">{feature}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (!isCurrent) {
                                                    handleSelectPlan(plan.id);
                                                }
                                            }}
                                            disabled={isCurrent || isProcessingCheckout}
                                            className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                                                isCurrent
                                                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                                    : isSelected
                                                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            }`}
                                        >
                                            {isProcessingCheckout && isSelected ? (
                                                <>
                                                    <Loader2 size={16} className="animate-spin" />
                                                    Processing...
                                                </>
                                            ) : isCurrent ? (
                                                'Current plan'
                                            ) : (
                                                'Select plan'
                                            )}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Footer */}
                    <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-between">
                        <p className="text-sm text-gray-500">
                            All plans include a 14-day money-back guarantee
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowPlansModal(false)}
                                disabled={isProcessingCheckout}
                                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Invoices Modal Component
    const InvoicesModal = () => (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={() => setShowInvoices(false)}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Invoice History</h2>
                        <p className="text-sm text-gray-500">View and download your invoices</p>
                    </div>
                    <button
                        onClick={() => setShowInvoices(false)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                {/* Invoices list */}
                <div className="p-6">
                    {isLoadingInvoices ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 size={32} className="animate-spin text-emerald-600" />
                        </div>
                    ) : invoices.length === 0 ? (
                        <div className="text-center py-12">
                            <Receipt size={48} className="mx-auto text-gray-300 mb-4" />
                            <p className="text-gray-500">No invoices yet</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {invoices.map((invoice) => (
                                <div
                                    key={invoice.id}
                                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-gray-200">
                                            <FileText size={18} className="text-gray-500" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">
                                                Invoice {invoice.number || invoice.id.slice(-8)}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {formatDate(invoice.createdAt)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="text-right">
                                            <p className="text-sm font-semibold text-gray-900">
                                                {formatCurrency(invoice.amount, invoice.currency)}
                                            </p>
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                invoice.status === 'paid'
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-yellow-100 text-yellow-700'
                                            }`}>
                                                {invoice.status}
                                            </span>
                                        </div>
                                        {invoice.pdfUrl && (
                                            <a
                                                href={invoice.pdfUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                                                title="Download PDF"
                                            >
                                                <Download size={18} className="text-gray-500" />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    // A single "used / limit" row in the Plan usage card. Shows "Unlimited" when
    // the limit is -1, "—" while usage is loading, and turns red when at the cap.

    // Subscription Card Component
    const SubscriptionCard = () => {
        if (isLoadingSubscription) {
            return (
                <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl p-6 text-white">
                    <div className="flex items-center justify-center py-8">
                        <Loader2 size={24} className="animate-spin text-white/70" />
                    </div>
                </div>
            );
        }

        if (subscriptionError) {
            return (
                <div className="bg-gradient-to-br from-gray-600 to-gray-700 rounded-2xl p-6 text-white">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertCircle size={18} />
                        <span className="text-sm">Failed to load</span>
                    </div>
                    <button
                        onClick={fetchSubscription}
                        className="text-sm underline hover:no-underline"
                    >
                        Try again
                    </button>
                </div>
            );
        }

        const currentPlan = availablePlans.find(p => p.id === subscription?.plan) || {
            name: subscription?.plan || 'Entry',
            price: 0
        };
        const PlanIcon = getPlanIcon(subscription?.plan);
        const needsAction = requiresAction(subscription?.status);
        // Every tier is paid now, so "has a subscription" is what these branches turn on —
        // not the plan name. An org with no subscription reports plan ENTRY with a null
        // stripeSubscriptionId, which is the unpaid fallback state.
        const isSubscribed = Boolean(subscription?.stripeSubscriptionId);

        return (
            <div className={`rounded-2xl p-6 text-white ${
                needsAction
                    ? 'bg-gradient-to-br from-amber-500 to-orange-600'
                    : 'bg-gradient-to-br from-emerald-600 to-teal-600'
            }`}>
                {/* Status warning */}
                {needsAction && (
                    <div className="flex items-center gap-2 mb-3 pb-3 border-b border-white/20">
                        <AlertTriangle size={16} />
                        <span className="text-sm font-medium">Payment issue - action required</span>
                    </div>
                )}

                <div className="flex items-center gap-2 mb-1">
                    <PlanIcon size={18} />
                    <h3 className="font-semibold">Current plan</h3>
                </div>
                <p className="text-2xl font-display font-bold mb-1">{currentPlan.name}</p>

                <p className="text-emerald-100 text-sm mb-2">
                    {formatCurrency(currentPlan.price, subscription?.currency || 'AUD')}/{subscription?.interval || 'month'}
                </p>

                {subscription?.currentPeriodEnd && isSubscribed && (
                    <div className="flex items-center gap-1.5 text-emerald-100 text-xs mb-4">
                        <Clock size={12} />
                        <span>
                            {subscription?.cancelAtPeriodEnd
                                ? `Cancels ${formatDate(subscription.currentPeriodEnd)}`
                                : `Renews ${formatDate(subscription.currentPeriodEnd)}`
                            }
                        </span>
                    </div>
                )}

                {!isSubscribed && (
                    <p className="text-emerald-100 text-sm mb-4">
                        Subscribe to activate your plan
                    </p>
                )}

                {/* Action buttons */}
                <div className="space-y-2">
                    <button
                        onClick={() => setShowPlansModal(true)}
                        className="w-full py-2.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-semibold transition-colors"
                    >
                        {isSubscribed ? 'Change plan' : 'View plans'}
                    </button>

                    {subscription?.stripeCustomerId && (
                        <button
                            onClick={handleOpenBillingPortal}
                            disabled={isOpeningPortal}
                            className="w-full py-2.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {isOpeningPortal ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <CreditCard size={14} />
                            )}
                            Manage billing
                        </button>
                    )}
                </div>
            </div>
        );
    };

    // Billing Quick Actions Card
    const BillingActionsCard = () => {
        if (!subscription?.stripeCustomerId) {
            return null;
        }

        return (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Billing</h3>
                <div className="space-y-3">
                    <button
                        onClick={handleOpenBillingPortal}
                        disabled={isOpeningPortal}
                        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                    >
                        <div className="flex items-center gap-3">
                            {isOpeningPortal ? (
                                <Loader2 size={18} className="text-gray-500 animate-spin" />
                            ) : (
                                <CreditCard size={18} className="text-gray-500" />
                            )}
                            <span className="text-sm font-medium text-gray-700">
                                {isOpeningPortal ? 'Opening...' : 'Payment methods'}
                            </span>
                        </div>
                        {!isOpeningPortal && <ExternalLink size={14} className="text-gray-400" />}
                    </button>

                    <button
                        onClick={handleViewInvoices}
                        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <FileText size={18} className="text-gray-500" />
                            <span className="text-sm font-medium text-gray-700">Invoice history</span>
                        </div>
                        <ExternalLink size={14} className="text-gray-400" />
                    </button>

                    <button
                        onClick={handleOpenBillingPortal}
                        disabled={isOpeningPortal}
                        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                    >
                        <div className="flex items-center gap-3">
                            {isOpeningPortal ? (
                                <Loader2 size={18} className="text-gray-500 animate-spin" />
                            ) : (
                                <RefreshCw size={18} className="text-gray-500" />
                            )}
                            <span className="text-sm font-medium text-gray-700">
                                {isOpeningPortal ? 'Opening...' : 'Cancel subscription'}
                            </span>
                        </div>
                        {!isOpeningPortal && <ExternalLink size={14} className="text-gray-400" />}
                    </button>
                </div>
            </div>
        );
    };

    // Loading state
    if (isLoading) {
        return (
            <PageShell
                title="Organisation"
                description="Manage your organisation details"
                icon={Building2}
            >
                <div className="flex items-center justify-center py-12">
                    <Loader2 size={32} className="animate-spin text-emerald-600" />
                </div>
            </PageShell>
        );
    }

    // Error state
    if (error && !organisation) {
        return (
            <PageShell
                title="Organisation"
                description="Manage your organisation details"
                icon={Building2}
            >
                <div className="flex flex-col items-center justify-center py-12">
                    <AlertCircle size={48} className="text-red-500 mb-4" />
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={fetchOrganisation}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                        Try again
                    </button>
                </div>
            </PageShell>
        );
    }

    return (
        <PageShell
            title="Organisation"
            description="Manage your organisation details"
            icon={Building2}
        >
            {/* Subscription Notification Banner */}
            {subscriptionNotification && (
                <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
                    subscriptionNotification.type === 'success'
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-blue-50 border border-blue-200'
                }`}>
                    <CheckCircle2
                        size={20}
                        className={subscriptionNotification.type === 'success' ? 'text-green-600' : 'text-blue-600'}
                    />
                    <span className={`font-medium ${
                        subscriptionNotification.type === 'success' ? 'text-green-800' : 'text-blue-800'
                    }`}>
                        {subscriptionNotification.message}
                    </span>
                    <button
                        onClick={() => setSubscriptionNotification(null)}
                        className={`ml-auto p-1 rounded-lg hover:bg-white/50 ${
                            subscriptionNotification.type === 'success' ? 'text-green-600' : 'text-blue-600'
                        }`}
                    >
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* Tabs */}
            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl mb-6 w-fit overflow-x-auto">
                {ORG_TABS.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                            activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        <tab.icon size={16} />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Organisation details */}
            {activeTab === 'details' && (
                <div>
                    <div className="bg-white rounded-2xl border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-semibold text-gray-900">Organisation details</h3>
                            {!isEditingDetails ? (
                                <button
                                    onClick={() => setIsEditingDetails(true)}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    <Edit2 size={14} />
                                    Edit
                                </button>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={handleCancelEdit}
                                        disabled={isSaving}
                                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        <X size={14} />
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        disabled={isSaving}
                                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        {isSaving ? (
                                            <Loader2 size={14} className="animate-spin" />
                                        ) : (
                                            <Save size={14} />
                                        )}
                                        Save
                                    </button>
                                </div>
                            )}
                        </div>

                        {saveError && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700">
                                <AlertCircle size={16} />
                                {saveError}
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                            {isEditingDetails ? (
                                <>
                                    <EditableField
                                        icon={Building2}
                                        label="Business name"
                                        field="name"
                                        value={formData.name}
                                        placeholder="Enter business name"
                                    />
                                    <div className="flex items-start gap-3 py-3">
                                        <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <Car size={16} className="text-gray-500" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <label className="text-xs text-gray-500 mb-1 block">Business type</label>
                                            <select
                                                value={formData.organisationType}
                                                onChange={(e) => handleInputChange('organisationType', e.target.value)}
                                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white cursor-pointer"
                                            >
                                                {ORGANISATION_TYPES.map(t => (
                                                    <option key={t.value} value={t.value}>{t.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <EditableField
                                        icon={Hash}
                                        label="ABN"
                                        field="abn"
                                        value={formData.abn}
                                        placeholder="11 digit ABN"
                                    />
                                    <EditableField
                                        icon={Phone}
                                        label="Phone"
                                        field="phone"
                                        value={formData.phone}
                                        placeholder="Contact phone number"
                                        type="tel"
                                    />
                                    <InfoItem
                                        icon={Mail}
                                        label="Email"
                                        value={organisation?.email}
                                    />
                                </>
                            ) : (
                                <>
                                    <InfoItem
                                        icon={Building2}
                                        label="Business name"
                                        value={organisation?.name}
                                    />
                                    <InfoItem
                                        icon={Car}
                                        label="Business type"
                                        value={ORGANISATION_TYPE_LABELS[organisation?.organisationType] || organisation?.organisationType}
                                    />
                                    <InfoItem
                                        icon={Hash}
                                        label="ABN"
                                        value={formatAbn(organisation?.abn)}
                                    />
                                    <InfoItem
                                        icon={Phone}
                                        label="Phone"
                                        value={organisation?.phone}
                                    />
                                    <InfoItem
                                        icon={Mail}
                                        label="Email"
                                        value={organisation?.email}
                                    />
                                </>
                            )}
                        </div>
                    </div>

                    {/* Address card */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 mt-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-semibold text-gray-900">Business address</h3>
                            {!isEditingAddress ? (
                                <button
                                    onClick={() => setIsEditingAddress(true)}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    <Edit2 size={14} />
                                    Edit
                                </button>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={handleCancelEdit}
                                        disabled={isSaving}
                                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        <X size={14} />
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        disabled={isSaving}
                                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        {isSaving ? (
                                            <Loader2 size={14} className="animate-spin" />
                                        ) : (
                                            <Save size={14} />
                                        )}
                                        Save
                                    </button>
                                </div>
                            )}
                        </div>

                        {isEditingAddress ? (
                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Street address</label>
                                    <input
                                        type="text"
                                        value={formData.address}
                                        onChange={(e) => handleInputChange('address', e.target.value)}
                                        placeholder="123 Business Street"
                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                    />
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                    <div className="col-span-2">
                                        <label className="text-xs text-gray-500 mb-1 block">Suburb/City</label>
                                        <input
                                            type="text"
                                            value={formData.suburb}
                                            onChange={(e) => handleInputChange('suburb', e.target.value)}
                                            placeholder="Melbourne"
                                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">State</label>
                                        <select
                                            value={formData.state}
                                            onChange={(e) => handleInputChange('state', e.target.value)}
                                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                                        >
                                            <option value="">Select</option>
                                            <option value="ACT">ACT</option>
                                            <option value="NSW">NSW</option>
                                            <option value="NT">NT</option>
                                            <option value="QLD">QLD</option>
                                            <option value="SA">SA</option>
                                            <option value="TAS">TAS</option>
                                            <option value="VIC">VIC</option>
                                            <option value="WA">WA</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Postcode</label>
                                        <input
                                            type="text"
                                            value={formData.postcode}
                                            onChange={(e) => handleInputChange('postcode', e.target.value)}
                                            placeholder="3000"
                                            maxLength={4}
                                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-start gap-3">
                                <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <MapPin size={16} className="text-gray-500" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-900">{organisation?.address || '—'}</p>
                                    {(organisation?.suburb || organisation?.state || organisation?.postcode) && (
                                        <p className="text-sm text-gray-900">
                                            {[organisation?.suburb, organisation?.state, organisation?.postcode]
                                                .filter(Boolean)
                                                .join(', ')}
                                        </p>
                                    )}
                                    <p className="text-sm text-gray-500">Australia</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── Working Hours card ──────────────────────────────────── */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 mt-6">

                        {/* Header */}
                        <div className="flex items-center justify-between mb-5">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
                                    <Calendar size={16} className="text-violet-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900">Working hours</h3>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        Wait time on paused jobs is counted only within these hours
                                    </p>
                                </div>
                            </div>

                            {!isEditingSchedule ? (
                                <button
                                    onClick={() => { setScheduleForm(schedule); setIsEditingSchedule(true); }}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    <Edit2 size={14} />
                                    Edit
                                </button>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={handleCancelScheduleEdit}
                                        disabled={isSavingSchedule}
                                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        <X size={14} />
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSaveSchedule}
                                        disabled={isSavingSchedule}
                                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        {isSavingSchedule
                                            ? <Loader2 size={14} className="animate-spin" />
                                            : <Save size={14} />
                                        }
                                        Save
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Save error */}
                        {scheduleSaveError && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700">
                                <AlertCircle size={15} />
                                {scheduleSaveError}
                            </div>
                        )}

                        {/* Body */}
                        {isLoadingSchedule ? (
                            <div className="flex items-center justify-center py-10">
                                <Loader2 size={24} className="animate-spin text-emerald-600" />
                            </div>
                        ) : scheduleError ? (
                            <div className="flex flex-col items-center gap-3 py-8 text-center">
                                <AlertCircle size={32} className="text-red-400" />
                                <p className="text-sm text-red-600">{scheduleError}</p>
                                <button
                                    onClick={fetchSchedule}
                                    className="text-sm text-emerald-600 hover:underline"
                                >
                                    Try again
                                </button>
                            </div>
                        ) : isEditingSchedule ? (

                            /* ── Edit mode ── */
                            <div className="space-y-1">
                                {scheduleForm.map(day => (
                                    <div
                                        key={day.dayOfWeek}
                                        className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 rounded-xl transition-colors ${
                                            day.isClosed ? 'bg-gray-50' : 'bg-emerald-50/40 border border-emerald-100'
                                        }`}
                                    >
                                        {/* Day name */}
                                        <span className={`text-sm font-semibold w-9 shrink-0 ${day.isClosed ? 'text-gray-400' : 'text-gray-800'}`}>
                                        {DAY_LABEL[day.dayOfWeek]}
                                    </span>

                                        {/* Open / Closed pill toggle */}
                                        <button
                                            onClick={() => handleScheduleDayChange(day.dayOfWeek, 'isClosed', !day.isClosed)}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors min-w-[76px] justify-center ${
                                                day.isClosed
                                                    ? 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                                                    : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                            }`}
                                        >
                                            {day.isClosed
                                                ? <><X size={11} /> Closed</>
                                                : <><Check size={11} /> Open</>
                                            }
                                        </button>

                                        {/* Time pickers — only when open */}
                                        {!day.isClosed ? (
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="time"
                                                    value={day.openTime}
                                                    onChange={e => handleScheduleDayChange(day.dayOfWeek, 'openTime', e.target.value)}
                                                    className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent w-[7.5rem]"
                                                />
                                                <span className="text-xs text-gray-400 shrink-0">to</span>
                                                <input
                                                    type="time"
                                                    value={day.closeTime}
                                                    onChange={e => handleScheduleDayChange(day.dayOfWeek, 'closeTime', e.target.value)}
                                                    className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent w-[7.5rem]"
                                                />
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-400 italic">No hours set</span>
                                        )}
                                    </div>
                                ))}
                            </div>

                        ) : (

                            /* ── View mode ── */
                            <div className="space-y-0.5">
                                {!scheduleConfigured && (
                                    <div className="flex items-center gap-2 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                                        <AlertTriangle size={14} className="shrink-0" />
                                        Default schedule active (Mon–Fri 8 am–5 pm). Edit to customise.
                                    </div>
                                )}
                                {schedule.map(day => (
                                    <div
                                        key={day.dayOfWeek}
                                        className={`flex items-center justify-between px-3 py-2 rounded-xl ${
                                            day.isClosed ? '' : 'bg-gray-50'
                                        }`}
                                    >
                                    <span className={`text-sm font-medium w-24 ${day.isClosed ? 'text-gray-400' : 'text-gray-700'}`}>
                                        {DAY_FULL[day.dayOfWeek]}
                                    </span>
                                        {day.isClosed ? (
                                            <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2.5 py-0.5 rounded-full">
                                            Closed
                                        </span>
                                        ) : (
                                            <span className="text-sm text-gray-900 tabular-nums">
                                            {day.openTime} – {day.closeTime}
                                        </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Migrating a workshop's history in from another product. */}
            {activeTab === 'imports' && <ImportsTab />}

            {/* Plan & billing */}
            {activeTab === 'plan' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                    {/* Quick stats — real usage vs. the current plan's (type-tuned) limits */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6">
                        <h3 className="font-semibold text-gray-900 mb-4">Plan usage</h3>
                        <div className="space-y-4">
                            <UsageStat label="Team members" res={usage?.staff} />
                            <UsageStat label="Vehicles" res={usage?.vehicles} />
                            {/* ⚠️ The figure counts what this workshop BUILT. The starter
                                services it was given do not consume the allowance, so
                                without this line the panel reads "0 / 1" beside a page
                                listing seven profiles and the only sense to make of that
                                is that something is broken. */}
                            <UsageStat
                                label="Service profiles"
                                res={usage?.workItemProfiles}
                                hint={usage?.systemServiceProfiles > 0
                                    ? `plus ${usage.systemServiceProfiles} starter service${usage.systemServiceProfiles === 1 ? '' : 's'}, which don't count`
                                    : null}
                            />
                            <UsageStat label="Customers" res={usage?.customers} />
                        </div>
                    </div>

                    {/* Plan info */}
                    <SubscriptionCard />

                    {/* Billing actions */}
                    <BillingActionsCard />


                    {/* Custom branding (logo on invoices & emails) */}
                    <BrandingCard />

                    {/* AI phone bookings (assigned Connect number + bot settings) */}
                    <VoiceBookingCard />
                </div>
            )}

            {/* Plans Modal */}
            {showPlansModal && <PlansModal />}

            {/* Invoices Modal */}
            {showInvoices && <InvoicesModal />}
        </PageShell>
    );
};

export default OrganisationPage;